import { hasNativeJSBridge } from '@/utils/bridge-runtime';

export type AttachmentPreviewKind = 'audio' | 'image' | 'video';

export type AttachmentPreviewContext = {
  fileName?: string | null;
  mimeType?: string | null;
};

export type AttachmentURLDiagnosticPayload = {
  status?: number;
  errorCode?: string;
  message?: string;
};

export type AttachmentPreviewFailure = {
  description?: string;
  diagnostic?: AttachmentURLDiagnosticPayload;
  label: string;
  presentation?: 'error' | 'file';
  source?: string | null;
};

const attachmentDiagnosticCacheTTL = 30_000;
const attachmentDiagnosticCache = new Map<
  string,
  { expiresAt: number; promise: Promise<AttachmentURLDiagnosticPayload> }
>();

function attachmentDiagnosticFromBody(
  status: number,
  body: unknown
): AttachmentURLDiagnosticPayload {
  if (!body || typeof body !== 'object') {
    return { status };
  }

  const payload = body as {
    error?: unknown;
    message?: unknown;
    code?: unknown;
  };
  if (payload.error && typeof payload.error === 'object') {
    const error = payload.error as { code?: unknown; message?: unknown };
    return {
      status,
      errorCode: typeof error.code === 'string' ? error.code : undefined,
      message: typeof error.message === 'string' ? error.message : undefined,
    };
  }
  if (typeof payload.error === 'string') {
    return { status, message: payload.error };
  }
  return {
    status,
    errorCode: typeof payload.code === 'string' ? payload.code : undefined,
    message: typeof payload.message === 'string' ? payload.message : undefined,
  };
}

async function diagnoseAttachmentURL(
  source: string
): Promise<AttachmentURLDiagnosticPayload> {
  const nativeDiagnose = window.jsb?.MessagesBridge?.diagnoseAttachmentURL;
  if (hasNativeJSBridge() && nativeDiagnose) {
    return nativeDiagnose(source);
  }

  try {
    const response = await fetch(source, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (response.ok) {
      return { status: response.status };
    }

    try {
      return attachmentDiagnosticFromBody(
        response.status,
        await response.json()
      );
    } catch {
      return { status: response.status };
    }
  } catch (error) {
    return {
      errorCode: 'network',
      message: error instanceof Error ? error.message : undefined,
    };
  }
}

export function diagnoseAttachmentURLOnce(
  source: string
): Promise<AttachmentURLDiagnosticPayload> {
  const now = Date.now();
  const cached = attachmentDiagnosticCache.get(source);
  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }

  const promise = diagnoseAttachmentURL(source).finally(() => {
    const current = attachmentDiagnosticCache.get(source);
    if (current?.promise === promise) {
      current.expiresAt = Date.now() + attachmentDiagnosticCacheTTL;
    }
  });
  attachmentDiagnosticCache.set(source, {
    expiresAt: now + attachmentDiagnosticCacheTTL,
    promise,
  });
  return promise;
}

function kindName(kind: AttachmentPreviewKind): string {
  switch (kind) {
    case 'audio':
      return 'Audio';
    case 'image':
      return 'Image';
    case 'video':
      return 'Video';
  }
}

function lowerKindName(kind: AttachmentPreviewKind): string {
  return kind;
}

function normalizedMimeType(value: string | null | undefined): string | null {
  return value?.split(';', 1)[0]?.trim().toLowerCase() || null;
}

function extensionFromFileName(
  value: string | null | undefined
): string | null {
  const path = value?.split(/[?#]/, 1)[0]?.trim();
  if (!path) {
    return null;
  }
  const index = path.lastIndexOf('.');
  if (index < 0 || index === path.length - 1) {
    return null;
  }
  return path.slice(index + 1).toLowerCase();
}

function describedFormat(context?: AttachmentPreviewContext): string {
  const mimeType = normalizedMimeType(context?.mimeType);
  if (mimeType && mimeType !== 'application/octet-stream') {
    return mimeType;
  }
  const extension = extensionFromFileName(context?.fileName);
  if (extension) {
    return `.${extension}`;
  }
  return 'this file type';
}

type PreviewFailureCopy = {
  description: string;
  label: string;
};

function shouldShowAsFile(diagnostic: AttachmentURLDiagnosticPayload): boolean {
  switch (diagnostic.errorCode) {
    case 'unsupported_media_type':
    case 'unsupported_preview':
    case 'unsupported_file_type':
      return true;
    default:
      break;
  }

  return (
    diagnostic.status === 200 ||
    diagnostic.status === 206 ||
    diagnostic.status === 415
  );
}

export function previewFailureLabel(
  kind: AttachmentPreviewKind,
  diagnostic: AttachmentURLDiagnosticPayload,
  context?: AttachmentPreviewContext
): string {
  return previewFailureCopy(kind, diagnostic, context).description;
}

export function previewFailureCopy(
  kind: AttachmentPreviewKind,
  diagnostic: AttachmentURLDiagnosticPayload,
  context?: AttachmentPreviewContext
): PreviewFailureCopy {
  const name = kindName(kind);
  const lowerName = lowerKindName(kind);
  const label = `${name} preview failed`;

  if (shouldShowAsFile(diagnostic)) {
    return {
      description: `${kindName(kind)} preview unavailable; showing ${describedFormat(
        context
      )} as a file.`,
      label: `${name} preview unavailable`,
    };
  }

  switch (diagnostic.errorCode) {
    case 'file_share_expired':
      return {
        description: 'The preview link has expired.',
        label,
      };
    case 'file_share_revoked':
      return {
        description: 'The preview link was revoked.',
        label,
      };
    case 'file_not_found':
      return {
        description: 'The preview source says the file was not found.',
        label,
      };
    case 'file_share_version_changed':
      return {
        description: 'The file changed after this preview link was created.',
        label,
      };
    case 'file_share_not_found':
      return {
        description: 'The preview share was not found.',
        label,
      };
    case 'agent_not_found':
      return {
        description: 'The source workspace was not found.',
        label,
      };
    case 'unauthorized':
      return {
        description: 'The preview source requires sign-in.',
        label,
      };
    case 'forbidden':
      return {
        description: 'The preview source denied access.',
        label,
      };
    case 'upstream_unavailable':
      return {
        description: 'The source workspace is temporarily unavailable.',
        label,
      };
    case 'network':
      return {
        description: `The browser could not reach the ${lowerName} preview source.`,
        label,
      };
    default:
      break;
  }

  switch (diagnostic.status) {
    case 400:
      return {
        description: 'The preview request is invalid.',
        label,
      };
    case 401:
      return {
        description: 'The preview source requires sign-in.',
        label,
      };
    case 403:
      return {
        description: 'The preview source denied access.',
        label,
      };
    case 404:
      return {
        description: 'The preview source says the file was not found.',
        label,
      };
    case 410:
      return {
        description: 'The preview link has expired.',
        label,
      };
    case 429:
      return {
        description: 'The preview source is rate limited.',
        label,
      };
    default:
      break;
  }

  if (typeof diagnostic.status === 'number' && diagnostic.status >= 500) {
    return {
      description: 'The preview source is temporarily unavailable.',
      label,
    };
  }

  if (typeof diagnostic.status === 'number') {
    return {
      description: `The preview source returned HTTP ${diagnostic.status}.`,
      label,
    };
  }

  return {
    description: 'The browser could not load this preview source.',
    label,
  };
}

export function sourceResolutionFailureLabel(
  kind: AttachmentPreviewKind,
  reason: string
): string {
  return sourceResolutionFailureCopy(kind, reason).description;
}

export function sourceResolutionFailureCopy(
  kind: AttachmentPreviewKind,
  reason: string
): PreviewFailureCopy {
  const name = kindName(kind);
  const label = `${name} preview unavailable`;

  switch (reason) {
    case 'missing-source':
      return {
        description:
          'The message did not include a preview URL or usable file reference.',
        label,
      };
    case 'native-authenticated-source-failed':
      return {
        description: 'Bridge could not prepare an authenticated preview URL.',
        label,
      };
    case 'native-local-source-failed':
      return {
        description: 'Bridge could not open the local preview file.',
        label,
      };
    default:
      return {
        description: 'The preview source could not be resolved.',
        label,
      };
  }
}

export async function diagnosePreviewFailure(
  kind: AttachmentPreviewKind,
  source: string,
  context?: AttachmentPreviewContext
): Promise<AttachmentPreviewFailure> {
  const diagnostic = await diagnoseAttachmentURLOnce(source);
  const copy = previewFailureCopy(kind, diagnostic, context);
  return {
    description: copy.description,
    diagnostic,
    label: copy.label,
    presentation: shouldShowAsFile(diagnostic) ? 'file' : 'error',
    source,
  };
}
